import { isRecetteBuild, requireRecetteApiBase } from '@shared/constants/buildProfile';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';

export const ACTIVATION_ISSUER = 'Nexxus Conseil';
export const ACTIVATION_AUDIENCE = 'ga.nexxus.erp';
export const ACTIVATION_PRODUCT_CODE = 'ERP';

export const ACTIVATION_ENDPOINT = 'https://api.nexxus.ga/v1/activations/activate';
export const RENEWAL_ENDPOINT = 'https://api.nexxus.ga/v1/activations/renew';
export const ENTITLEMENTS_ENDPOINT = 'https://api.nexxus.ga/v1/activations/entitlements';
export const STATUS_ENDPOINT = 'https://api.nexxus.ga/v1/activations/status';
export const DEACTIVATE_ENDPOINT = 'https://api.nexxus.ga/v1/activations/deactivate';
export const SUPPORT_UNLOCK_VERIFY_PATH = '/v1/licence/support-unlock/verify';

/** Clé commerciale de développement (seed licensing-api). */
export const DEV_COMMERCIAL_KEY = 'NXS-DEV0-TEST-0000-0001';

export const DEFAULT_DEV_LICENSING_API_URL = 'http://localhost:3000';
export const DEFAULT_PROD_LICENSING_API_URL = 'https://api.nexxus.ga';

/** Origine du site Cloud (navigateur) ; null hors site Cloud (application poste, développement). */
function cloudWebOrigin(): string | null {
  if (!isBrowserCloudHost() || typeof window === 'undefined') return null;
  const origin = window.location.origin;
  return origin && origin !== 'null' ? origin.replace(/\/$/, '') : null;
}

/**
 * Base URL de nexxus-licensing-api (sans slash final).
 * Toujours définie : sans repli production, la session d'installation et le
 * catalogue de modules restaient inertes dans les binaires construits sans
 * `VITE_LICENSING_API_URL`.
 */
export function resolveLicensingApiBase(): string {
  // Site Cloud (navigateur) : la CSP du site impose `connect-src 'self'`. Les appels au serveur de licences passent
  // donc par le proxy du site, sur sa propre origine (voir infra/cloud-web/nginx.conf).
  const webOrigin = cloudWebOrigin();
  if (webOrigin) return webOrigin;
  if (isRecetteBuild()) {
    return requireRecetteApiBase(
      import.meta.env.VITE_LICENSING_API_URL as string | undefined,
      'VITE_LICENSING_API_URL',
    );
  }
  const fromEnv = import.meta.env.VITE_LICENSING_API_URL as string | undefined;
  if (fromEnv?.trim()) return fromEnv.trim().replace(/\/$/, '');
  if (import.meta.env.DEV) return DEFAULT_DEV_LICENSING_API_URL;
  return DEFAULT_PROD_LICENSING_API_URL;
}

/**
 * Bases licensing : URL configurée, puis production si elle diffère.
 * Build de recette : UNIQUEMENT l'URL de recette (jamais de repli vers la production, pour
 * qu'une clé ou une activation de test n'atteigne pas les serveurs commerciaux).
 */
export function resolveLicensingApiBases(): string[] {
  const webOrigin = cloudWebOrigin();
  if (webOrigin) return [webOrigin];
  const configured = resolveLicensingApiBase();
  if (isRecetteBuild()) return [configured];
  if (configured === DEFAULT_PROD_LICENSING_API_URL) return [configured];
  return [configured, DEFAULT_PROD_LICENSING_API_URL];
}

/** Base configurée puis, en repli, l'API de production. */
function resolveEndpoints(path: string): string[] {
  return resolveLicensingApiBases().map((base) => `${base}${path}`);
}

export function resolveActivationEndpoints(): string[] {
  return resolveEndpoints('/v1/activations/activate');
}

export function resolveRenewalEndpoints(): string[] {
  return resolveEndpoints('/v1/activations/renew');
}

export function resolveEntitlementsEndpoints(): string[] {
  return resolveEndpoints('/v1/activations/entitlements');
}

export function resolveStatusEndpoints(): string[] {
  return resolveEndpoints('/v1/activations/status');
}

export function resolveDeactivateEndpoints(): string[] {
  return resolveEndpoints('/v1/activations/deactivate');
}

export function resolveSupportUnlockVerifyEndpoints(): string[] {
  return resolveEndpoints(SUPPORT_UNLOCK_VERIFY_PATH);
}

/** Dépôt copie Hybride (live sync + nocturne). */
export function resolveHybridBackupEndpoints(): string[] {
  return resolveEndpoints('/v1/hybrid-backups');
}

/** @deprecated Contrat v1 : utiliser resolveHybridBackupLatestMetaEndpoints(). */
export function resolveHybridBackupLatestEndpoints(): string[] {
  return resolveEndpoints('/v1/hybrid-backups/latest');
}

/** @deprecated Contrat v1 : utiliser resolveHybridBackupItemEndpoints(id). */
export function resolveHybridBackupRestoreEndpoints(): string[] {
  return resolveEndpoints('/v1/hybrid-backups/latest/content');
}

/** Métadonnées de la dernière sauvegarde chiffrée (GET). */
export function resolveHybridBackupLatestMetaEndpoints(): string[] {
  return resolveEndpoints('/v1/hybrid-backups/latest/meta');
}

/** Liste des sauvegardes chiffrées disponibles — métadonnées seulement (GET). */
export function resolveHybridBackupListEndpoints(): string[] {
  return resolveEndpoints('/v1/hybrid-backups');
}

/** Octets d'un coffre chiffré (GET /v1/hybrid-backups/:id). L'identifiant est encodé. */
export function resolveHybridBackupItemEndpoints(id: string): string[] {
  return resolveEndpoints(`/v1/hybrid-backups/${encodeURIComponent(id)}`);
}

/** @deprecated Préférer resolveActivationEndpoints() */
export const DEV_ACTIVATION_ENDPOINT = `${DEFAULT_DEV_LICENSING_API_URL}/v1/activations/activate`;
/** @deprecated Préférer resolveRenewalEndpoints() */
export const DEV_RENEWAL_ENDPOINT = `${DEFAULT_DEV_LICENSING_API_URL}/v1/activations/renew`;
